// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MdOutlinePictureAsPdf, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { VscSend } from 'react-icons/vsc';
import { HeartIcon, CommentIcon, ShareIcon } from "./Icons";
import { Avatar } from '../../reusable/components/avatar';
import { useCreator, useUser } from "../../backend/hooks";

import type { MediaFile } from './FeedScreen';

interface Comment {
  id: string;
  user: string;
  username: string;
  content: string;
  timestamp: string;
  likes: number;
  replies?: Comment[];
  profilePicture?: string;
}

interface LinkPreview {
  title: string;
  description: string;
  url: string;
  image?: string | null;
  source?: string;
}

interface PostItemProps {
  post: {
    id: string;
    user: string;
    username: string;
    userId?: string;
    profilePicture?: string;
    tag: string;
    title?: string;
    content: string;
    timestamp: string;
    media?: MediaFile[];
    linkPreview?: LinkPreview;
    likes: number;
    comments: number;
    reposts: number;
    affiliation?: string;
  };
  selected?: boolean;
  currentUserName?: string;
  onDelete?: (postId: string) => void;
  isFollowing?: boolean;
  onToggleFollow?: (userId?: string) => void;
}

// Recursive component for rendering nested replies
const NestedReply: React.FC<{
  reply: Comment;
  depth: number;
  currentUserName?: string;
  likedComments: Set<string>;
  onLike: (id: string) => void;
  onReply: (id: string) => void;
  replyingTo: string | null;
  replyText: string;
  setReplyText: (text: string) => void;
  handleReply: (commentId: string) => void;
  creatorProfile?: any;
  loggedInUser?: any;
}> = ({ reply, depth, currentUserName, likedComments, onLike, onReply, replyingTo, replyText, setReplyText, handleReply, creatorProfile, loggedInUser }) => {
  const maxDepth = 5; // Limit nesting to prevent infinite recursion
  
  const isReplyCurrentUser = reply.user === 'You' || reply.user === currentUserName;
  const getAvatarUrl = (pic: any) => {
    if (Array.isArray(pic)) return pic[0] || "";
    return pic || "";
  };
  const currentUserAvatar = getAvatarUrl(creatorProfile?.profilePicture) || 
                           getAvatarUrl(loggedInUser?.profilePicture) || 
                           getAvatarUrl(loggedInUser?.profilePhotoUrl) || 
                           "";
  
  return (
    <div className={`reply-item-inline depth-${Math.min(depth, maxDepth)}`}>
      <Avatar 
        url={isReplyCurrentUser ? currentUserAvatar : getAvatarUrl(reply.profilePicture)} 
        height={depth === 0 ? "24px" : "20px"} 
        width={depth === 0 ? "24px" : "20px"} 
        componentKey={`reply-${reply.id}`} 
        name={reply.user} 
      />
      <div className="reply-content-inline">
        <div className="reply-header-inline">
          <span className="reply-author-inline">{reply.user}</span>
          <span className="reply-timestamp-inline">{reply.timestamp}</span>
        </div>
        <div className="reply-text-inline">{reply.content}</div>
        <div className="reply-actions-inline">
          <button 
            className={`reply-like-btn-inline ${likedComments.has(reply.id) ? 'liked' : ''}`}
            onClick={() => onLike(reply.id)}
          >
            <HeartIcon 
              size={10} 
              filled={likedComments.has(reply.id)} 
              color={likedComments.has(reply.id) ? "#FF1744" : "#6b7280"} 
            />
            <span>{reply.likes + (likedComments.has(reply.id) ? 1 : 0)}</span>
          </button>
          {depth < maxDepth && (
            <button 
              className="comment-reply-btn-inline"
              onClick={() => onReply(reply.id)}
            >
              Reply
            </button>
          )}
        </div>

        {/* Reply Input */}
        {replyingTo === reply.id && (
          <div className="reply-input-inline-container">
            <Avatar 
              url={currentUserAvatar} 
              height="24px" 
              width="24px" 
              componentKey={`reply-input-${reply.id}`} 
              name={currentUserName || 'You'} 
            />
            <div className="reply-input-inline-wrapper">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={`Reply to ${reply.user}...`}
                className="reply-input-inline"
                rows={1}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = target.scrollHeight + 'px';
                }}
              />
              <div className="reply-actions-container-inline">
                <button className="reply-action-btn-inline" title="Add photo">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                </button>
                <button className="reply-action-btn-inline" title="Add emoji">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                    <line x1="9" y1="9" x2="9.01" y2="9"></line>
                    <line x1="15" y1="9" x2="15.01" y2="9"></line>
                  </svg>
                </button>
                <button 
                  className="reply-submit-btn-inline"
                  onClick={() => handleReply(reply.id)}
                  disabled={!replyText.trim()}
                >
                  {/* @ts-ignore */}
                  <VscSend />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Nested Replies */}
        {reply.replies && reply.replies.length > 0 && (
          <div className="replies-section-inline">
            {reply.replies.map((nestedReply) => (
              <NestedReply
                key={nestedReply.id}
                reply={nestedReply}
                depth={depth + 1}
                currentUserName={currentUserName}
                likedComments={likedComments}
                onLike={onLike}
                onReply={onReply}
                replyingTo={replyingTo}
                replyText={replyText}
                setReplyText={setReplyText}
                handleReply={handleReply}                creatorProfile={creatorProfile}
                loggedInUser={loggedInUser}              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Media Carousel component for Instagram-style manual sliding
const MediaCarousel: React.FC<{ media: MediaFile[] }> = ({ media }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [aspectRatio, setAspectRatio] = useState<'1/1' | '4/5' | '16/9'>('1/1');
  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Auto-detect aspect ratio from the first media item
  useEffect(() => {
    if (media && media.length > 0) {
      const firstMedia = media[0];
      if (firstMedia.type === 'image') {
        const img = new Image();
        img.src = firstMedia.preview;
        img.onload = () => {
          const ratio = img.width / img.height;
          if (ratio < 0.85) setAspectRatio('4/5');
          else if (ratio > 1.2) setAspectRatio('16/9');
          else setAspectRatio('1/1');
        };
      } else if (firstMedia.type === 'video') {
        const video = document.createElement('video');
        video.src = firstMedia.preview;
        video.onloadedmetadata = () => {
          const ratio = video.videoWidth / video.videoHeight;
          if (ratio < 0.85) setAspectRatio('4/5');
          else if (ratio > 1.2) setAspectRatio('16/9');
          else setAspectRatio('1/1');
        };
      }
    }
  }, [media]);

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentIndex < media.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          left: scrollRef.current.clientWidth * nextIndex,
          behavior: 'smooth'
        });
      }
    }
  };

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentIndex > 0) {
      const prevIndex = currentIndex - 1;
      setCurrentIndex(prevIndex);
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          left: scrollRef.current.clientWidth * prevIndex,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const index = Math.round(scrollRef.current.scrollLeft / scrollRef.current.clientWidth);
      if (index !== currentIndex) {
        setCurrentIndex(index);
      }
    }
  };

  return (
    <div className={`post-media-carousel-wrapper ratio-${aspectRatio.replace('/', '-')}`}>
      <div 
        className="post-media-carousel-container" 
        ref={scrollRef}
        onScroll={handleScroll}
        style={{
          display: 'flex',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none', // Hide scrollbar for Firefox
          msOverflowStyle: 'none', // Hide scrollbar for IE/Edge
          WebkitOverflowScrolling: 'touch',
          aspectRatio: aspectRatio, // Dynamic aspect ratio: 1/1, 4/5, or 16/9
          background: '#FFFFFF', // Clean white background
          borderRadius: '0px', 
          maxHeight: '80vh', 
          width: '100%',
          margin: '0 auto'
        }}
      >
        {media.map((item, idx) => (
          <div 
            key={idx} 
            className="post-media-slide"
            style={{
              flex: '0 0 100%',
              width: '100%',
              height: '100%',
              scrollSnapAlign: 'start',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {item.type === 'image' ? (
              <img 
                src={item.preview} 
                alt={`Post media ${idx + 1}`} 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: aspectRatio === '1/1' ? 'cover' : 'contain', 
                  display: 'block'
                }} 
              />
            ) : item.type === 'video' ? (
              <video 
                src={item.preview} 
                controls 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'contain',
                  display: 'block'
                }} 
              />
            ) : (
              <div 
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                  color: '#1F1937'
                }}
              >
                <MdOutlinePictureAsPdf size={48} />
                <span>PDF Document</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {currentIndex > 0 && (
        <button className="carousel-nav-btn prev" onClick={handlePrev}>
          <MdChevronLeft size={24} />
        </button>
      )}
      {currentIndex < media.length - 1 && (
        <button className="carousel-nav-btn next" onClick={handleNext}>
          <MdChevronRight size={24} />
        </button>
      )}

      {media.length > 1 && (
        <div className="carousel-dots">
          {media.map((_, idx) => (
            <div 
              key={idx} 
              className={`carousel-dot ${idx === currentIndex ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const PostItem: React.FC<PostItemProps & { onComment?: () => void; showCommentInput?: boolean; showComments?: boolean }> = ({ post, selected = false, onComment, showCommentInput, showComments = false, currentUserName, onDelete, isFollowing, onToggleFollow }) => {
  const navigate = useNavigate();
  const { creatorProfile } = useCreator();
  const { loggedInUser } = useUser();
  const commentsEnabled = false;
  
  const creatorName = creatorProfile ? `${creatorProfile.firstName} ${creatorProfile.lastName}` : "";
  
  const isCurrentUser = post.user === 'You' || 
                        (currentUserName && post.user === currentUserName) || 
                        (loggedInUser?.userId && post.userId === loggedInUser?.userId) ||
                        (creatorName && post.user === creatorName) ||
                        (loggedInUser?.name && post.user === loggedInUser.name);

  // Normalize profile picture URL
  const getAvatarUrl = (pic: any) => {
    if (Array.isArray(pic)) return pic[0] || "";
    return pic || "";
  };

  const currentUserAvatar = getAvatarUrl(creatorProfile?.profilePicture) || 
                           getAvatarUrl(loggedInUser?.profilePicture) || 
                           getAvatarUrl(loggedInUser?.profilePhotoUrl) || 
                           "";

  const [showMenu, setShowMenu] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const moreBtnRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (!showMenu) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        moreBtnRef.current &&
        !moreBtnRef.current.contains(e.target as Node)
      ) {
        setShowMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);
  const [isLiked, setIsLiked] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  // Comment states
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [likedComments, setLikedComments] = useState<Set<string>>(new Set());
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  
  // Sample comments data for this post
  const sampleComments = React.useMemo(() => [
    {
      id: 'comment-1',
      user: 'Emily Johnson',
      username: 'emily_j_design', 
      content: 'Absolutely stunning work! The way you\'ve balanced the natural light with the warm wood tones is incredible. Can you share what type of quartz you used for the countertops?',
      timestamp: '2h',
      likes: 12,
      replies: [
        {
          id: 'reply-1',
          user: post.user,
          username: post.username,
          content: 'Thank you so much! I used Caesarstone in Pure White. The key was getting the right thickness - went with 3cm for that substantial look.',
          timestamp: '1h',
          likes: 8
        }
      ]
    },
    {
      id: 'comment-2',
      user: 'Michael Chen',
      username: 'mike_builds',
      content: 'Love this design! The functionality aspect is so important in kitchen renovations. Did you have to deal with any structural changes for the layout?',
      timestamp: '3h',
      likes: 7
    },
    {
      id: 'comment-3',
      user: 'Lisa Rodriguez', 
      username: 'lisa_interiors',
      content: 'The oak cabinets are gorgeous! 😍 This is exactly the look I\'m going for in my upcoming project. Mind sharing the cabinet maker?',
      timestamp: '4h',
      likes: 15
    }
  ], [post.user, post.username]);

  // Initialize comments when showComments becomes true
  React.useEffect(() => {
    if (showComments && comments.length === 0) {
      setComments(sampleComments);
    }
  }, [showComments, comments.length, sampleComments]);

  // Comment handling functions
  const handleCommentLike = (commentId: string) => {
    const newLikedComments = new Set(likedComments);
    if (likedComments.has(commentId)) {
      newLikedComments.delete(commentId);
    } else {
      newLikedComments.add(commentId);
    }
    setLikedComments(newLikedComments);
  };

  const handlePostComment = () => {
    if (newComment.trim()) {
      const comment: Comment = {
        id: `comment-${Date.now()}`,
        user: currentUserName || 'You',
        username: (currentUserName || 'You').toLowerCase().replace(' ', '_'),
        content: newComment,
        timestamp: 'now',
        likes: 0,
        replies: []
      };
      setComments([comment, ...comments]);
      setNewComment('');
    }
  };

  const handleReply = (commentId: string, parentId?: string) => {
    if (replyText.trim()) {
      const reply: Comment = {
        id: `reply-${Date.now()}`,
        user: currentUserName || 'You',
        username: (currentUserName || 'You').toLowerCase().replace(' ', '_'),
        content: replyText,
        timestamp: 'now',
        likes: 0,
        replies: []
      };

      const addReplyToComment = (comment: Comment): Comment => {
        if (comment.id === commentId) {
          return {
            ...comment,
            replies: [...(comment.replies || []), reply]
          };
        }
        if (comment.replies) {
          return {
            ...comment,
            replies: comment.replies.map(addReplyToComment)
          };
        }
        return comment;
      };

      setComments(prevComments => prevComments.map(addReplyToComment));
      setReplyText('');
      setReplyingTo(null);
    }
  };

  // Toggle like state
  const handleLike = () => {
    setIsLiked(!isLiked);
  };

  // Menu portal component to avoid clipping inside transformed/overflow parents
  const MenuPortal: React.FC<{ anchorRef: React.RefObject<HTMLElement>; onClose: () => void; children: React.ReactNode }> = ({ anchorRef, onClose, children }) => {
    const portalRef = React.useRef<HTMLDivElement | null>(null);
    const [style, setStyle] = useState<React.CSSProperties>({});

    React.useLayoutEffect(() => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      
      // Position menu below the anchor button
      const top = rect.bottom + 8;

      // Align to the right of the button
      const right = window.innerWidth - rect.right;

      setStyle({ position: 'fixed', right, top, zIndex: 20000 });
    }, [anchorRef]);

    React.useEffect(() => {
      function onGlobalClick(e: MouseEvent) {
        const portalEl = portalRef.current;
        const anchor = anchorRef.current;
        if (!portalEl) return;
        if (anchor && anchor.contains(e.target as Node)) return;
        if (!portalEl.contains(e.target as Node)) onClose();
      }
      document.addEventListener('mousedown', onGlobalClick);
      return () => document.removeEventListener('mousedown', onGlobalClick);
    }, [onClose, anchorRef]);

    return createPortal(
      <div ref={portalRef as any} style={style} className="post-more-menu-portal">
        {children}
      </div>,
      document.body
    );
  };

  // Toggle read more/less
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Handle share functionality
  const handleShare = async () => {
    const postUrl = `${window.location.origin}/feed#post-${post.id}`;
    const shareText = `Check out this post by ${post.user}:\n${post.content.substring(0, 100)}...`;

    // Try Web Share API first (mobile friendly)
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Kydoscope Post',
          text: shareText,
          url: postUrl
        });
      } catch (err) {
        console.log('Share cancelled or failed:', err);
      }
    } else {
      // Fallback: Copy to clipboard and show confirmation
      try {
        await navigator.clipboard.writeText(postUrl);
        alert('Post link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy:', err);
        alert('Could not copy link. Try again!');
      }
    }
  };

  return (
    <div id={`post-${post.id}`} className={`post-item ${selected ? 'selected' : ''}`}> 
      <div className="post-header">
        <div className="post-header-left">
          <div 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/profile/${post.username}`);
            }}
            style={{ cursor: 'pointer' }}
          >
            <Avatar 
              url={isCurrentUser ? currentUserAvatar : (getAvatarUrl(post.profilePicture) || "")} 
              height="40px" 
              width="40px" 
              componentKey={`post-${post.id}-avatar`} 
              name={post.user} 
            />
          </div>
          <div className="post-author-info">
            <div className="post-author-line">
              <strong 
                className="post-author-name"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/profile/${post.username}`);
                }}
                style={{ cursor: 'pointer' }}
              >
                {post.user === 'You' ? (currentUserName === 'You' ? 'Karan Sheth' : currentUserName) : post.user}
              </strong>
              {loggedInUser?.userId && !isCurrentUser && (
                <button
                  className={`post-follow-link ${isFollowing ? 'following' : ''} ${post.userId ? '' : 'disabled'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (post.userId && onToggleFollow) onToggleFollow(post.userId);
                  }}
                  disabled={!post.userId}
                  title={!post.userId ? 'Follow unavailable' : undefined}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
              )}
            </div>
            <div className="post-meta-line">
              <span className="post-author-handle">
                {post.user === 'You' || post.user === currentUserName 
                  ? (post.affiliation || 'Kydoscope Interiors') // Default explicitly for 'You' if missing
                  : (post.affiliation)}
              </span>
              <span className="post-separator">•</span>
              <span className="post-category-tag">{post.tag}</span>
              <span className="post-separator">•</span>
              <span className="post-timestamp">{post.timestamp}</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
            <button ref={moreBtnRef} className="post-action-btn post-more" style={{ padding: '0 8px', background: 'none', border: 'none', cursor: 'pointer' }} onClick={(e) => {
              e.stopPropagation();
              setShowMenu(prev => !prev);
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1F1937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="5" cy="12" r="1.5" />
                <circle cx="12" cy="12" r="1.5" />
                <circle cx="19" cy="12" r="1.5" />
              </svg>
            </button>
            {/* @ts-ignore */}
            {showMenu && (
              <MenuPortal anchorRef={moreBtnRef} onClose={() => setShowMenu(false)}>
                <div ref={menuRef} className="post-more-menu-inner">
                  <button className="post-more-menu-btn" onClick={() => { navigator.clipboard.writeText(window.location.href); setShowMenu(false); }}>Copy Link</button>
                  <button className="post-more-menu-btn" onClick={() => { setShowMenu(false); }}>Bookmark</button>
                  <button className="post-more-menu-btn" onClick={() => { setShowMenu(false); }}>Not Interested</button>
                  {(post.user === currentUserName || post.user === 'You') && (
                    <button
                      className="post-more-menu-btn"
                      style={{ color: '#FF1744' }}
                      onClick={() => {
                        if (onDelete) onDelete(post.id);
                        setShowMenu(false);
                      }}
                    >
                      Delete Post
                    </button>
                  )}
                </div>
              </MenuPortal>
            )}
        </div>
      </div>

      {(post.title || post.content) && (
        <div className="post-content">
          {post.title && (
            <h2 className="post-title-display">{post.title}</h2>
          )}
          <div className="post-text">
            {(() => {
              // Helper to render markdown links
              const renderMarkdownLinks = (text: string) => {
                // [alt](url)
                const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
                let lastIndex = 0;
                const elements: React.ReactNode[] = [];
                let match;
                while ((match = regex.exec(text)) !== null) {
                  // Text before link
                  if (match.index > lastIndex) {
                    elements.push(text.slice(lastIndex, match.index));
                  }
                  // Link: show alternate text, clickable, styled
                  elements.push(
                    <a
                      key={match[2] + match.index}
                      href={match[2]}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#0ea5e9', textDecoration: 'underline', fontWeight: 500 }}
                    >
                      {match[1]}
                    </a>
                  );
                  lastIndex = regex.lastIndex;
                }
                // Remaining text
                if (lastIndex < text.length) {
                  elements.push(text.slice(lastIndex));
                }
                // If no markdown link, check for plain URL
                if (elements.length === 1 && typeof elements[0] === 'string') {
                  // Simple URL regex
                  const urlRegex = /(https?:\/\/[^\s]+)/g;
                  const urlParts = (elements[0] as string).split(urlRegex);
                  return urlParts.map((part, i) => {
                    if (urlRegex.test(part)) {
                      return (
                        <a
                          key={part + i}
                          href={part}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#0ea5e9', textDecoration: 'underline', fontWeight: 500 }}
                        >
                          {part}
                        </a>
                      );
                    }
                    return part;
                  });
                }
                return elements;
              };
              // Expand/collapse logic
              const textToShow = isExpanded || post.content.length <= 200
                ? post.content
                : post.content.substring(0, 200) + '...';
              return <>
                {renderMarkdownLinks(textToShow)}
                {post.content.length > 200 && (
                  <button 
                    className="read-more-btn" 
                    onClick={toggleExpanded}
                  >
                    {isExpanded ? ' Read Less' : ' Read More'}
                  </button>
                )}
              </>;
            })()}
          </div>
        </div>
      )}

      {/* Link - show as clickable URL text only */}
      {post.linkPreview && (
        <div style={{ marginBottom: '16px' }}>
          <a 
            href={post.linkPreview.url} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: '#1f1937', 
              fontSize: '15px', 
              fontWeight: '600',
              textDecoration: 'none',
              wordBreak: 'break-all',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>
            {post.linkPreview.title || post.linkPreview.url}
          </a>
        </div>
      )}

      {/* Standardized Carousel for Media */}
      {post.media && post.media.length > 0 && (
        <MediaCarousel media={post.media} />
      )}

      {/* Image Modal */}
      {showImageModal && post.media && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setShowImageModal(false)}
        >
          <button
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
            onClick={() => setShowImageModal(false)}
          >
            ×
          </button>
          
          {post.media.length > 1 && selectedImageIndex > 0 && (
            <button
              style={{
                position: 'absolute',
                left: '20px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '24px',
                zIndex: 10000
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImageIndex(prev => prev - 1);
              }}
            >
              ‹
            </button>
          )}

          <img
            src={post.media[selectedImageIndex]?.preview}
            alt="Full size"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain'
            }}
            onClick={(e) => e.stopPropagation()}
          />

          {post.media.length > 1 && selectedImageIndex < post.media.length - 1 && (
            <button
              style={{
                position: 'absolute',
                right: '20px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '24px',
                zIndex: 10000
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImageIndex(prev => prev + 1);
              }}
            >
              ›
            </button>
          )}

          <div
            style={{
              position: 'absolute',
              bottom: '20px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {selectedImageIndex + 1} / {post.media.length}
          </div>
        </div>
      )}

  <div className="post-footer">
    <div className="post-footer-row" style={{display:'flex',alignItems:'center',width:'100%'}}>
      {/* All actions and menu in one horizontal flex row */}
      <button className="post-action-btn" onClick={handleLike}>
        <HeartIcon size={16} filled={isLiked} color={isLiked ? "#FF1744" : "#1F1937"} />
        <span>{post.likes + (isLiked ? 1 : 0)}</span>
      </button>
      {commentsEnabled && (
        <button className="post-action-btn" onClick={onComment}>
          <CommentIcon size={16} color="#1F1937" />
          <span>{post.comments}</span>
        </button>
      )}
      <button className="post-action-btn" onClick={handleShare}>
        <ShareIcon size={16} color="#1F1937" />
        <span>{post.reposts}</span>
      </button>
    </div>
  </div>
  
  {/* Inline Comments Section */}
  {commentsEnabled && showComments && (
    <div className="inline-comments-section">
      {/* Add Comment Input */}
      <div className="add-comment-inline">
        <Avatar 
          url={currentUserAvatar} 
          height="32px" 
          width="32px" 
          componentKey={`comment-input-${post.id}`} 
          name={currentUserName || 'You'} 
        />
        <div className="comment-input-inline-wrapper">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write comment..."
            className="comment-input-inline"
            rows={1}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = target.scrollHeight + 'px';
            }}
          />
          <div className="comment-actions-inline">
            <button className="comment-action-btn-inline" title="Add photo">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
            </button>
            <button className="comment-action-btn-inline" title="Add emoji">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                <line x1="9" y1="9" x2="9.01" y2="9"></line>
                <line x1="15" y1="9" x2="15.01" y2="9"></line>
              </svg>
            </button>
            <button 
              className="comment-submit-btn-inline"
              onClick={handlePostComment}
              disabled={!newComment.trim()}
            >
              {/* @ts-ignore */}
              <VscSend />
            </button>
          </div>
        </div>
      </div>

      {/* Comments List */}
      {comments.length > 0 && (
        <div className="comments-list-inline">
          <div className="comments-title-inline">
            {comments.length + comments.reduce((acc, comment) => acc + (comment.replies?.length || 0), 0)} comments
          </div>
          
          {comments.map((comment) => (
            <div key={comment.id} className="comment-item-inline">
              <div className="comment-main-inline">
                <Avatar 
                  url={(comment.user === 'You' || comment.user === currentUserName) ? currentUserAvatar : getAvatarUrl(comment.avatar)} 
                  height="28px" 
                  width="28px" 
                  componentKey={`comment-${comment.id}`} 
                  name={comment.user} 
                />
                <div className="comment-content-inline">
                  <div className="comment-header-inline">
                    <span className="comment-author-inline">{comment.user}</span>
                    <span className="comment-timestamp-inline">{comment.timestamp}</span>
                  </div>
                  <div className="comment-text-inline">{comment.content}</div>
                  <div className="comment-actions-inline-footer">
                    <button 
                      className={`comment-like-btn-inline ${likedComments.has(comment.id) ? 'liked' : ''}`}
                      onClick={() => handleCommentLike(comment.id)}
                    >
                      <HeartIcon 
                        size={12} 
                        filled={likedComments.has(comment.id)} 
                        color={likedComments.has(comment.id) ? "#FF1744" : "#6b7280"} 
                      />
                      <span>{comment.likes + (likedComments.has(comment.id) ? 1 : 0)}</span>
                    </button>
                    <button 
                      className="comment-reply-btn-inline"
                      onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                    >
                      Reply
                    </button>
                  </div>

                  {/* Reply Input */}
                  {replyingTo === comment.id && (
                    <div className="reply-input-inline-container">
                      <Avatar 
                        url={currentUserAvatar} 
                        height="28px" 
                        width="28px" 
                        componentKey={`reply-${comment.id}`} 
                        name={currentUserName || 'You'} 
                      />
                      <div className="reply-input-inline-wrapper">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder={`Reply to ${comment.user}...`}
                          className="reply-input-inline"
                          rows={1}
                          onInput={(e) => {
                            const target = e.target as HTMLTextAreaElement;
                            target.style.height = 'auto';
                            target.style.height = target.scrollHeight + 'px';
                          }}
                        />
                        <div className="reply-actions-container-inline">
                          <button className="reply-action-btn-inline" title="Add photo">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              <circle cx="8.5" cy="8.5" r="1.5"></circle>
                              <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                          </button>
                          <button className="reply-action-btn-inline" title="Add emoji">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"></circle>
                              <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                              <line x1="9" y1="9" x2="9.01" y2="9"></line>
                              <line x1="15" y1="9" x2="15.01" y2="9"></line>
                            </svg>
                          </button>
                          <button 
                            className="reply-submit-btn-inline"
                            onClick={() => handleReply(comment.id)}
                            disabled={!replyText.trim()}
                          >
                            {/* @ts-ignore */}
                            <VscSend />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Replies */}
              {comment.replies && comment.replies.length > 0 && (
                <div className="replies-section-inline">
                  {comment.replies.map((reply) => (
                    <NestedReply
                      key={reply.id}
                      reply={reply}
                      depth={0}
                      currentUserName={currentUserName}
                      likedComments={likedComments}
                      onLike={handleCommentLike}
                      onReply={(replyId) => setReplyingTo(replyingTo === replyId ? null : replyId)}
                      replyingTo={replyingTo}
                      replyText={replyText}
                      setReplyText={setReplyText}
                      handleReply={handleReply}                      creatorProfile={creatorProfile}
                      loggedInUser={loggedInUser}                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )}
    </div>
  );
};

export default PostItem;

